import React, { useEffect, useState } from 'react';
import { Grid, Typography, TextField, IconButton } from '@mui/material';
import Paper, { paperClasses } from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import { SearchOutlined } from '@mui/icons-material';
import { observer } from 'mobx-react';
import { logs } from '@cosmjs/stargate';
import { useStores } from '@stores';
import { useLocation, useNavigate } from 'react-router-dom';
import { ITEM_TYPES } from '@constants/itemTypes';
import { Decimal } from '@cosmjs/math';
import classnames from 'classnames';
import _ from 'lodash';
import {
  copyText,
  useCopied,
  getShortAddress,
  useMediaQuery,
  isLessThanOneThousandth,
  floorRounding,
  findAttribute,
  splitCamelCase,
  numberWithSeparator,
  findEvent,
} from '@utils';
import { MILLION_MULTIPLIER, RPC_URL_DEV } from '@constants/system';
import BeeSpinnerLoading from '@components/BeeSpinnerLoading';
import Table from '@components/Table';
import Layout from '@layout';
import { BEARS } from '@api';
import InformationPopup from '@components/InformationPopup';
import BearAvatar from '@assets/AddressPage/bear_avatar.png';
import CopyIcon from '@assets/AddressPage/copy_icon.svg';
import styles from './addressInfo.module.scss';

export const TitlePaper = ({ title, value, secondValue = '', isTitle = false, isInnerBlock = false, className = {} }) => {
  return (
    <Grid
      container
      justifyContent="space-between"
      alignItems="center"
      className={classnames(
        styles.TitlePaperContainer,
        isTitle && styles.TitlePaperContainer_mainTitle,
        className.Container,
        isInnerBlock && styles.TitlePaperContainer_isInnerBlock,
      )}
    >
      <p className={isTitle ? styles.TitlePaper : styles.SubTitlePaper}>{title}</p>
      <p className={styles.ValuePaper}>
        {!_.isEmpty(value) || _.isNumber(value)
          ? secondValue
            ? `${value} (${isLessThanOneThousandth(secondValue)}%)`
            : value
          : ''}
      </p>
    </Grid>
  );
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${paperClasses.root}`]: {
    borderRadius: '10px',
    boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.03)',
    paddingBottom: '10px',
  },
}));

export const AddressPageInfo = observer(() => {
  const stores = useStores();
  const location = useLocation();
  const navigate = useNavigate();
  const { copied, setCopied } = useCopied();
  const isWidth600 = useMediaQuery('(min-width: 600px)');
  const [address, setAddress] = useState(
    (location.state?.address || window.location.pathname.split('/address/')[1]).toLowerCase(),
  );

  const [isFirstLoading, setFirstLoading] = useState(true);
  const [isLoading, setLoading] = useState(true);
  const [isTablesLoading, setTablesLoading] = useState(true);
  const [isTablesPaginationLoading, setTablesPaginationLoading] = useState(false);
  const [isExistWallet, setExistWallet] = useState(true);
  const [balanceCoins, setBalanceCoins] = useState(0);
  const [poolCones, setPoolCones] = useState(0);
  const [bearsInfo, setBearsInfo] = useState();
  const [params, setParams] = useState();
  const [honeyPowerBearState, setHoneyPowerBearState] = useState(null);

  const [apiariesInfo, setApiariesInfo] = useState([]);
  const [apiariesPage, setApiariesPage] = useState(0);
  const [apiariesRowsPerPage, setApiariesRowsPerPage] = useState(5);

  const [treesInfo, setTreesInfo] = useState([]);
  const [treesPage, setTreesPage] = useState(0);
  const [treesRowsPerPage, setTreesRowsPerPage] = useState(5);

  const [decorationsInfo, setDecorationsInfo] = useState([]);
  const [decorationsPage, setDecorationsPage] = useState(0);
  const [decorationsRowsPerPage, setDecorationsRowsPerPage] = useState(5);

  const [beesInfo, setBeesInfo] = useState([]);
  const [beesPage, setBeesPage] = useState(0);
  const [beesRowsPerPage, setBeesRowsPerPage] = useState(5);

  const [fieldsInfo, setFieldsInfo] = useState([]);
  const [fields, setFields] = useState();
  const [fieldsPage, setFieldsPage] = useState(0);
  const [fieldsRowsPerPage, setFieldsRowsPerPage] = useState(5);

  const [fieldTilesInfo, setFieldTilesInfo] = useState([]);
  const [fieldTilesPage, setFieldTilesPage] = useState(0);
  const [fieldTilesRowsPerPage, setFieldTilesRowsPerPage] = useState(5);
  const [fieldTilesRowsTotalCount, setFieldTilesRowsTotalCount] = useState(0);

  const [stakedCones, setStakedCones] = useState(0);
  const [stackedConesValidators, setStackedConesValidators] = useState([]);
  const [totalStakingRewardsState, setTotalStakingRewardsState] = useState(0);
  const [unstakingConesState, setUnstakingConesState] = useState(0);

  const [isTransactionsTablePaginationLoading, setTransactionsTablePaginationLoading] =
    useState(false);
  const [latestTransactionsInfo, setLatestTransactionsInfo] = useState([]);
  /* latestTransactionsPage after refactoring become simply flag to 
  starting LatestTransactionsTable's useEffect & 
  disabling disabledPrevButton */
  const [latestTransactionsPage, setLatestTransactionsPage] = useState(0);
  const [isTransactionsTablesLoading, setTransactionsTablesLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [transactionsRowsPerPage, setTransactionsRowsPerPage] = useState(5);

  const [notEmptyTables, setNotEmptyTables] = useState(null);
  const [isActiveTable, setActiveTable] = useState(null);

  const [blockHeight, setBlockHeight] = useState(null);
  const [prevRequestedHigherBlockHeightArray, setPrevRequestedHigherBlockHeightArray] = useState(
    [],
  );
  const [finalRequestedHigherBlockHeight, setFinalRequestedHigherBlockHeight] = useState(null);
  const [finalRequestedLowerBlockHeight, setFinalRequestedLowerBlockHeight] = useState(null);

  useEffect(() => {
    (async () => {
      if (stores.isDevelop) {
        await stores.configAnonymousClient(RPC_URL_DEV);
      }
    })();
  }, [stores.isDevelop]);

  //* **** useEffect: Bear Statistics (coins balance) **** *//
  useEffect(() => {
    const address = (
      location.state?.address || window.location.pathname.split('/address/')[1]
    ).toLowerCase();
    setAddress(address);
    if (stores.isAnonymousClientConfigured) {
      (async () => {
        try {
          setLoading(true);
          const balance = await BEARS.getBalance({
            address,
          });
          const params = await BEARS.getParams();
          const poolCones = await BEARS.getAllStakedAndUnstakingCones();
          const bearsIds = await BEARS.getBearsId({
            address,
          });
          const bearsInfo = await BEARS.getBearsInfo({
            ids: bearsIds.bears,
          });
          const honeyPowerBear = bearsIds?.bears
            ? await BEARS.getHoneyPowerBear({
                bearId: bearsIds.bears[0],
              })
            : null;
          const stackedConesValidators = await BEARS.getStakedCones({
            address,
          });
          setStackedConesValidators(stackedConesValidators);
          const stakedCones = JSON.parse(JSON.stringify(stackedConesValidators)).reduce((sum, val) => {
            return sum + Number(val.balance.amount);
          }, 0);

          setStakedCones(stakedCones);
          const totalStakingRewards = await BEARS.getTotalStakingRewards({ address });
          setTotalStakingRewardsState(
            totalStakingRewards.length
              ? floorRounding(totalStakingRewards[0]?.amount / MILLION_MULTIPLIER)
              : 0,
          );
          const unstakingCones = await BEARS.getUnstakingCones({ address });
          const blockHeight = await BEARS.getBlockHeights();
          setBlockHeight(blockHeight);
          setUnstakingConesState(unstakingCones);
          setHoneyPowerBearState(floorRounding(honeyPowerBear?.result * params?.blocksPerHour));
          setBalanceCoins(balance);
          setPoolCones(poolCones);
          setBearsInfo(bearsInfo);
          setExistWallet(true);
          setParams(params);
        } catch (err) {
          console.log(err);
          setExistWallet(false);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, location.state?.address]);

  //* **** useEffect: Bear info **** *//
  useEffect(() => {
    if (bearsInfo?.length && stores.isAnonymousClientConfigured && params) {
      (async () => {
        try {
          setTablesLoading(true);

          if (bearsInfo[0]?.apiaries.length) {
            const apiariesPagesChunk = _.chunk(bearsInfo[0]?.apiaries, apiariesRowsPerPage);
            const apiaries = await BEARS.getApiaries({
              ids: apiariesPagesChunk[apiariesPage],
            });

            setApiariesInfo(
              apiaries.map((apiary) => [
                apiary?.id,
                apiary?.position?.rowId,
                apiary?.position?.columnId,
                apiary?.spaceOccupied,
                floorRounding(apiary?.countHoney),
                floorRounding(apiary?.honeyPower * params?.blocksPerHour),
                apiary?.params?.apiaryType,
                apiary?.params?.deleteReward[0]?.amount / MILLION_MULTIPLIER,
                Decimal.fromAtomics(apiary?.params?.maxHoney, 18).toFloatApproximation() /
                  MILLION_MULTIPLIER,
                apiary?.params?.price[0]?.amount / MILLION_MULTIPLIER,
                apiary?.params?.spaceAvailable,
                `${
                  apiary?.cycleHistory[0]?.bees && apiary?.cycleHistory[0]?.height
                    ? apiary?.cycleHistory.map(
                        (item, index) =>
                          `\n cycle: ${index}, bees(id): ${item?.bees}, height: ${item?.height}`,
                      )
                    : '-'
                }`,
              ]),
            );
          }

          if (bearsInfo[0]?.trees.length) {
            const treesPagesChunk = _.chunk(bearsInfo[0]?.trees, treesRowsPerPage);
            const trees = await BEARS.getTrees({
              ids: treesPagesChunk[treesPage],
            });
            setTreesInfo(
              trees.map((tree) => {
                return [
                  tree?.id,
                  tree?.position?.fieldId,
                  tree?.position?.rowId,
                  tree?.position?.columnId,
                  tree?.params?.treeType,
                  tree?.params?.price[0]?.amount / MILLION_MULTIPLIER,
                  Decimal.fromAtomics(tree?.params?.airSupply, 18).toFloatApproximation(),
                  tree?.params?.reward[0]?.amount,
                ];
              }),
            );
          }

          if (bearsInfo[0]?.bees.length) {
            const beesPagesChunk = _.chunk(bearsInfo[0]?.bees, beesRowsPerPage);
            const bees = await BEARS.getBees({
              ids: beesPagesChunk[beesPage],
            });
            // TODO getHoneyPowerBeePerBlock add in getBees
            const beesPowerHoney = bees
              ? await Promise.all(
                  bees.map(async (bee) => {
                    const honeyPowerBee = await BEARS.getHoneyPowerBeePerBlock({
                      beeType: bee?.params?.beeType,
                    });
                    return {
                      beeId: bee.id,
                      powerHoney: honeyPowerBee.result * params?.blocksPerHour,
                    };
                  }),
                )
              : null;
            setBeesInfo(
              bees.map((bee) => {
                const honeyPerHour = floorRounding(
                  (Decimal.fromAtomics(bee?.params?.honeyPerBlock, 18).toFloatApproximation() /
                    MILLION_MULTIPLIER) *
                    params.blocksPerHour,
                  2,
                );
                return [
                  bee?.id,
                  bee?.name,
                  bee?.params?.beeType,
                  bee?.params?.price[0]?.amount / MILLION_MULTIPLIER,
                  Decimal.fromAtomics(bee?.params?.airConsume, 18).toFloatApproximation(),
                  Decimal.fromAtomics(bee?.params?.airCountDependency, 18).toFloatApproximation(),
                  bee?.params?.bodySize,
                  String(honeyPerHour)[String(honeyPerHour).length - 1] === '0'
                    ? honeyPerHour.slice(0, -1)
                    : honeyPerHour,
                  floorRounding(beesPowerHoney.find((item) => item.beeId === bee.id).powerHoney),
                ];
              }),
            );
          }

          if (bearsInfo[0]?.decorations.length) {
            const decorationsPagesChunk = _.chunk(
              bearsInfo[0]?.decorations,
              decorationsRowsPerPage,
            );
            const decorations = await BEARS.getDecorations({
              ids: decorationsPagesChunk[decorationsPage],
            });
            setDecorationsInfo(
              decorations.map((decoration) => {
                return [
                  decoration?.id,
                  decoration?.position?.fieldId,
                  decoration?.position?.rowId,
                  decoration?.position?.columnId,
                  decoration?.params?.decorationType,
                  decoration?.params?.price[0]?.amount / MILLION_MULTIPLIER,
                ];
              }),
            );
          }

          if (bearsInfo[0]?.fields.length) {
            const fieldsPagesChunk = _.chunk(bearsInfo[0]?.fields, fieldsRowsPerPage);
            const fields = await BEARS.getFields({
              ids: fieldsPagesChunk[fieldsPage],
            });
            setFields(fields);
            setFieldsInfo(
              fields.map((field) => {
                return [
                  field?.id,
                  field?.countTiles,
                  field?.params?.fieldType,
                  field?.params?.priceTile[0]?.amount / MILLION_MULTIPLIER,
                ];
              }),
            );

            const fieldTilesPagesChunk = _.chunk(fields[0]?.rows, fieldTilesRowsPerPage);
            setFieldTilesRowsTotalCount(fields[0]?.rows.length);
            setFieldTilesInfo(
              fieldTilesPagesChunk[fieldTilesPage].map((row, indexRow) => {
                const rowOfTilesArr = row?.columns.map((tile, indexColumn) => {
                  if (Object.keys(tile).length) {
                    return [
                      `row = ${
                        indexRow + fieldTilesPage * fieldTilesRowsPerPage
                      },\ncolumn = ${indexColumn},\nid = ${tile?.item?.itemId},\ntype = ${
                        ITEM_TYPES[tile?.item?.itemType]
                      }`,
                    ];
                  }
                  return '-';
                });
                return rowOfTilesArr;
              }),
            );
          }
          setFirstLoading(false);
        } catch (err) {
          console.log(err);
        } finally {
          setTablesLoading(false);
        }
      })();
    } else {
      setTablesLoading(false);
    }
  }, [stores.isAnonymousClientConfigured, bearsInfo, params]);

  //* **** useEffect: Bear apiary table **** *//
  useEffect(() => {
    if (
      bearsInfo?.length &&
      bearsInfo[0]?.apiaries.length &&
      stores.isAnonymousClientConfigured &&
      params &&
      !isFirstLoading
    ) {
      (async () => {
        try {
          setTablesPaginationLoading(true);

          const apiariesPagesChunk = _.chunk(bearsInfo[0]?.apiaries, apiariesRowsPerPage);
          const apiaries = await BEARS.getApiaries({
            ids: apiariesPagesChunk[apiariesPage],
          });

          setApiariesInfo(
            apiaries.map((apiary) => [
              apiary?.id,
              apiary?.position?.rowId,
              apiary?.position?.columnId,
              apiary?.spaceOccupied,
              floorRounding(apiary?.countHoney),
              floorRounding(apiary?.honeyPower * params?.blocksPerHour),
              apiary?.params?.apiaryType,
              apiary?.params?.deleteReward[0]?.amount / MILLION_MULTIPLIER,
              Decimal.fromAtomics(apiary?.params?.maxHoney, 18).toFloatApproximation() /
                MILLION_MULTIPLIER,
              apiary?.params?.price[0]?.amount / MILLION_MULTIPLIER,
              apiary?.params?.spaceAvailable,
              `${
                apiary?.cycleHistory[0]?.bees && apiary?.cycleHistory[0]?.height
                  ? apiary?.cycleHistory.map(
                      (item, index) =>
                        `\n cycle: ${index}, bees(id): ${item?.bees}, height: ${item?.height}`,
                    )
                  : '-'
              }`,
            ]),
          );
        } catch (err) {
          console.log(err);
        } finally {
          setTablesPaginationLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, apiariesPage, apiariesRowsPerPage]);
  
  //* **** useEffect: Bear tree table **** *//
  useEffect(() => {
    if (
      bearsInfo?.length &&
      bearsInfo[0]?.trees.length &&
      stores.isAnonymousClientConfigured &&
      params &&
      !isFirstLoading
    ) {
      (async () => {
        try {
          setTablesPaginationLoading(true);

          const treesPagesChunk = _.chunk(bearsInfo[0]?.trees, treesRowsPerPage);
          const trees = await BEARS.getTrees({
            ids: treesPagesChunk[treesPage],
          });
          setTreesInfo(
            trees.map((tree) => {
              return [
                tree?.id,
                tree?.position?.fieldId,
                tree?.position?.rowId,
                tree?.position?.columnId,
                tree?.params?.treeType,
                tree?.params?.price[0]?.amount / MILLION_MULTIPLIER,
                Decimal.fromAtomics(tree?.params?.airSupply, 18).toFloatApproximation(),
                tree?.params?.reward[0]?.amount,
              ];
            }),
          );
        } catch (err) {
          console.log(err);
        } finally {
          setTablesPaginationLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, treesPage, treesRowsPerPage]);

  //* **** useEffect: Bear bee table **** *//
  useEffect(() => {
    if (
      bearsInfo?.length &&
      bearsInfo[0]?.bees.length &&
      stores.isAnonymousClientConfigured &&
      params &&
      !isFirstLoading
    ) {
      (async () => {
        try {
          setTablesPaginationLoading(true);

          const beesPagesChunk = _.chunk(bearsInfo[0]?.bees, beesRowsPerPage);
          const bees = await BEARS.getBees({
            ids: beesPagesChunk[beesPage],
          });
          // TODO getHoneyPowerBeePerBlock add in getBees
          const beesPowerHoney = bees
            ? await Promise.all(
                bees.map(async (bee) => {
                  const honeyPowerBee = await BEARS.getHoneyPowerBeePerBlock({
                    beeType: bee?.params?.beeType,
                  });
                  return {
                    beeId: bee.id,
                    powerHoney: honeyPowerBee.result * params?.blocksPerHour,
                  };
                }),
              )
            : null;
          setBeesInfo(
            bees.map((bee) => {
              const honeyPerHour = floorRounding(
                (Decimal.fromAtomics(bee?.params?.honeyPerBlock, 18).toFloatApproximation() /
                  MILLION_MULTIPLIER) *
                  params.blocksPerHour,
                2,
              );
              return [
                bee?.id,
                bee?.name,
                bee?.params?.beeType,
                bee?.params?.price[0]?.amount / MILLION_MULTIPLIER,
                Decimal.fromAtomics(bee?.params?.airConsume, 18).toFloatApproximation(),
                Decimal.fromAtomics(bee?.params?.airCountDependency, 18).toFloatApproximation(),
                bee?.params?.bodySize,
                String(honeyPerHour)[String(honeyPerHour).length - 1] === '0'
                  ? honeyPerHour.slice(0, -1)
                  : honeyPerHour,
                floorRounding(beesPowerHoney.find((item) => item.beeId === bee.id).powerHoney),
              ];
            }),
          );
        } catch (err) {
          console.log(err);
        } finally {
          setTablesPaginationLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, beesPage, beesRowsPerPage]);

  //* **** useEffect: Bear decoration table **** *//
  useEffect(() => {
    if (
      bearsInfo?.length &&
      bearsInfo[0]?.decorations.length &&
      stores.isAnonymousClientConfigured &&
      params &&
      !isFirstLoading
    ) {
      (async () => {
        try {
          setTablesPaginationLoading(true);

          const decorationsPagesChunk = _.chunk(bearsInfo[0]?.decorations, decorationsRowsPerPage);
          const decorations = await BEARS.getDecorations({
            ids: decorationsPagesChunk[decorationsPage],
          });
          setDecorationsInfo(
            decorations.map((decoration) => {
              return [
                decoration?.id,
                decoration?.position?.fieldId,
                decoration?.position?.rowId,
                decoration?.position?.columnId,
                decoration?.params?.decorationType,
                decoration?.params?.price[0]?.amount / MILLION_MULTIPLIER,
              ];
            }),
          );
        } catch (err) {
          console.log(err);
        } finally {
          setTablesPaginationLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, decorationsPage, decorationsRowsPerPage]);

  //* **** useEffect: Bear field table **** *//
  useEffect(() => {
    if (
      bearsInfo?.length &&
      bearsInfo[0]?.fields.length &&
      stores.isAnonymousClientConfigured &&
      params &&
      !isFirstLoading
    ) {
      (async () => {
        try {
          setTablesPaginationLoading(true);

          const fieldsPagesChunk = _.chunk(bearsInfo[0]?.fields, fieldsRowsPerPage);
          const fields = await BEARS.getFields({
            ids: fieldsPagesChunk[fieldsPage],
          });
          setFields(fields);
          setFieldsInfo(
            fields.map((field) => {
              return [
                field?.id,
                field?.countTiles,
                field?.params?.fieldType,
                field?.params?.priceTile[0]?.amount / MILLION_MULTIPLIER,
              ];
            }),
          );

          const fieldTilesPagesChunk = _.chunk(fields[0]?.rows, fieldTilesRowsPerPage);
          setFieldTilesRowsTotalCount(fields[0]?.rows.length);
          setFieldTilesInfo(
            fieldTilesPagesChunk[fieldTilesPage].map((row, indexRow) => {
              const rowOfTilesArr = row?.columns.map((tile, indexColumn) => {
                if (Object.keys(tile).length) {
                  return [
                    `row = ${
                      indexRow + fieldTilesPage * fieldTilesRowsPerPage
                    },\ncolumn = ${indexColumn},\nid = ${tile?.item?.itemId},\ntype = ${
                      ITEM_TYPES[tile?.item?.itemType]
                    }`,
                  ];
                }
                return '-';
              });
              return rowOfTilesArr;
            }),
          );
        } catch (err) {
          console.log(err);
        } finally {
          setTablesPaginationLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, fieldsPage, fieldsRowsPerPage]);

  //* **** useEffect: Bear field table **** *//
  useEffect(() => {
    if (
      bearsInfo?.length &&
      bearsInfo[0]?.fields.length &&
      fields?.length &&
      stores.isAnonymousClientConfigured &&
      params &&
      !isFirstLoading
    ) {
      const fieldTilesPagesChunk = _.chunk(fields[0]?.rows, fieldTilesRowsPerPage);
      setFieldTilesRowsTotalCount(fields[0]?.rows.length);
      setFieldTilesInfo(
        fieldTilesPagesChunk[fieldTilesPage].map((row, indexRow) => {
          const rowOfTilesArr = row?.columns.map((tile, indexColumn) => {
            if (Object.keys(tile).length) {
              return [
                `row = ${
                  indexRow + fieldTilesPage * fieldTilesRowsPerPage
                },\ncolumn = ${indexColumn},\nid = ${tile?.item?.itemId},\ntype = ${
                  ITEM_TYPES[tile?.item?.itemType]
                }`,
              ];
            }
            return '-';
          });
          return rowOfTilesArr;
        }),
      );
    }
  }, [stores.isAnonymousClientConfigured, fields, fieldTilesPage, fieldTilesRowsPerPage]);

  //* **** useEffect: Bears info **** *//
  useEffect(() => {
    if (bearsInfo?.length) {
      const notEmptyTablesArray = [
        bearsInfo[0]?.apiaries?.length && 'Apiaries Info',
        bearsInfo[0]?.trees?.length && 'Trees Info',
        bearsInfo[0]?.bees?.length && 'Bees Info',
        bearsInfo[0]?.decorations?.length && 'Decorations Info',
        bearsInfo[0]?.fields?.length && 'Fields Info',
      ];
      setNotEmptyTables(notEmptyTablesArray);
      setActiveTable(notEmptyTablesArray.find((item) => item));
    }
  }, [bearsInfo]);

  //* **** useEffect: Latest transactions table **** *//
  useEffect(() => {
    if (stores.isAnonymousClientConfigured && address) {
      (async () => {
        try {
          setTransactionsTablePaginationLoading(true);
          const result = await BEARS.getTransactionsNew({ address, page: latestTransactionsPage, limit: transactionsRowsPerPage });
          setTotalCount(1000);
          setLatestTransactionsInfo(result.txsInfo);
        } catch (err) {
          console.log(err);
        } finally {
          setTransactionsTablesLoading(false);
          setTransactionsTablePaginationLoading(false);
        }
      })();
    }
  }, [stores.isAnonymousClientConfigured, latestTransactionsPage, transactionsRowsPerPage, address]);

  const onCopyAddress = () => {
    copyText({ text: address });
    setCopied();
  };

  const SearchClick = () => {
    (async () => {
      try {
        const bearName = await BEARS.getBearName(address);
        const bearsInfo = await BEARS.getBearsInfo({
          ids: [bearName?.bearId],
        });
        if (bearsInfo?.length && bearsInfo[0].owner) {
          setAddress(bearsInfo[0].owner);
          navigate(`/address/${bearsInfo[0].owner}`, {
            state: { address: bearsInfo[0].owner },
          });
        } else {
          navigate(`/address/${address}`, { state: { address } });
        }
      } catch (error) {
        navigate(`/address/${address}`, { state: { address } });
      }
    })();
  };
  const onKeyHandle = (event) => {
    if (event.charCode === 13 && event.target.value !== '') {
      SearchClick();
    }
  };
  const onChangeAddress = (event) => {
    setAddress(event.target.value.toLowerCase());
  };

  return (
    <Layout>
      {isLoading ? (
        <BeeSpinnerLoading
          classNames={{
            Container: styles.LoadingPageContainer,
            Spinner: styles.LoadingPageSpinner,
          }}
        />
      ) : (
        <Grid
          container
          sx={{ maxWidth: '1660px', width: '100%' }}
          p={{ xs: '20px', sm: '30px', md: '40px 60px', lg: '50px 130px' }}
          className={styles.MainContainer}
        >
          {isExistWallet ? (
            <>
              <Grid
                container
                sx={{
                  maxWidth: '1660px',
                  width: '100%',
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
                mb={{ xs: '20px', sm: '30px', md: '40px' }}
                alignItems={{ lg: 'center' }}
                justifyContent={{ xs: 'center', lg: 'unset' }}
                className={styles.AccountBlock}
              >
                <Grid
                  item
                  flexDirection={{ xs: 'column', lg: 'row' }}
                  alignItems={{ xs: 'center', lg: 'center' }}
                  lg={7}
                  p={{ xs: '20px' }}
                  className={styles.LeftInfoBlock}
                >
                  <img src={BearAvatar} alt="bear_avatar" className={styles.AvatarImage} />
                  <Grid
                    container
                    flexDirection="column"
                    alignItems={{ xs: 'center', lg: 'unset' }}
                    className={styles.SubtitleBlock}
                  >
                    <Typography variant="p" component="p" className={styles.Subtitle}>
                      Account
                    </Typography>
                    <div
                      className={styles.AccountAddress}
                      onClick={() => onCopyAddress()}
                      type="button"
                      role="button"
                      tabIndex="0"
                      onKeyPress={() => {
                        onCopyAddress();
                      }}
                    >
                      <p className={styles.AccountAddressText}>
                        {isWidth600 ? address : getShortAddress(address)}
                      </p>
                      <div className={styles.AccountAddress__CopyButtonContainer}>
                        <img src={CopyIcon} alt="copy_icon" className={styles.CopyIcon} />
                        {copied && (
                          <InformationPopup
                            info="Copied"
                            classNames={{
                              Container: styles.AccountAddress__InfoPopupContainer,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                container
                spacing={{ xs: 2, lg: 6 }}
                justifyContent={{ xs: 'unset', lg: 'space-between' }}
              >
                <Grid item xs={12} md={4}>
                  <p className={styles.AccountSummaryTitle}>Account Summary</p>
                  <Grid item flexDirection="column">
                    {!!bearsInfo && bearsInfo.length > 0 && (
                      <StyledPaper elevation={3}>
                        <TitlePaper
                          title="Bear Info"
                          isTitle
                          className={{ Container: styles.PaperTitleInfoContainer__Bear }}
                        />
                        <TitlePaper title="id:" value={bearsInfo[0].id} />
                        <TitlePaper title="Address:" value={getShortAddress(bearsInfo[0].owner)} />
                        <TitlePaper
                          title="Name:"
                          value={
                            bearsInfo[0].name.length > 20
                              ? getShortAddress(bearsInfo[0].name)
                              : bearsInfo[0].name
                          }
                        />
                        <TitlePaper title="Honey Power Bear:" value={honeyPowerBearState} />
                        <TitlePaper title="Apiaries count:" value={bearsInfo[0].apiaries.length} />
                        <TitlePaper title="Fields count:" value={bearsInfo[0].fields.length} />
                        <TitlePaper title="Bees count:" value={bearsInfo[0].bees.length} />
                        <TitlePaper title="Trees count:" value={bearsInfo[0].trees.length} />
                        <TitlePaper
                          title="Decorations count:"
                          value={bearsInfo[0].decorations.length}
                        />
                      </StyledPaper>
                    )}
                    <StyledPaper elevation={3} className={styles.AccountSummaryInfoTable}>
                      <TitlePaper
                        title="Coins Balance"
                        isTitle
                        className={{ Container: styles.PaperTitleInfoContainer__Coins }}
                      />
                      <TitlePaper
                        title="HoneyCoins Balance:"
                        value={balanceCoins.honey}
                        className={{ Container: styles.PaperTitleInfoContainer__Item }}
                      />
                      <TitlePaper
                        title="CONE Balance:"
                        value={balanceCoins.cone}
                        className={{ Container: styles.PaperTitleInfoContainer__Item }}
                      />
                      <TitlePaper
                        title="Staked Cones:"
                        value={stakedCones}
                        secondValue={stakedCones / (poolCones?.bondedTokens / 100)}
                        className={{ Container: styles.PaperTitleInfoContainer__Item }}
                      />
                      {stackedConesValidators && stackedConesValidators.length > 0 && (
                        <div className={styles.PaperTitleInfoStakedValidatorsContainer}>
                          {stackedConesValidators.map((stakeValidator, index) => {
                            return (
                              <TitlePaper
                                key={`stakeValidator_${stakeValidator.delegation.validatorAddress}`}
                                title={`- staked cones, validator ${index + 1}:`}
                                value={stakeValidator.balance.amount}
                                isInnerBlock
                                className={{ Container: styles.PaperTitleInfoContainer__Item }}
                              />
                            );
                          })}
                        </div>
                      )}
                      <TitlePaper
                        title="Total Staking Rewards:"
                        value={totalStakingRewardsState}
                        className={{ Container: styles.PaperTitleInfoContainer__Item }}
                      />
                      <TitlePaper
                        title="Unstaking Cones:"
                        value={unstakingConesState}
                        className={{ Container: styles.PaperTitleInfoContainer__Item }}
                      />
                    </StyledPaper>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={8}>
                  {isTablesLoading ? (
                    <BeeSpinnerLoading classNames={{ Spinner: styles.LoadingTablesContainer }} />
                  ) : bearsInfo?.length > 0 ? (
                    <>
                      <Grid
                        sx={{
                          flexDirection: { xs: 'column', sm: 'row' },
                        }}
                        container
                        justifyContent={{ xs: 'space-between', lg: 'flex-start' }}
                        alignItems="center"
                      >
                        {notEmptyTables?.length &&
                          notEmptyTables.map((item) => {
                            if (item) {
                              return (
                                <Typography
                                  variant="p"
                                  component="p"
                                  key={item}
                                  onClick={() => setActiveTable(item)}
                                  sx={{
                                    mb: { xs: '8px', sm: '0' },
                                    mr: {
                                      lg: '24px',
                                      xl: '60px',
                                    },
                                  }}
                                  className={classnames(
                                    styles.TablesLinksText,
                                    isActiveTable === item && styles.TablesLinksText__active,
                                  )}
                                >
                                  {item}
                                </Typography>
                              );
                            }
                            return null;
                          })}
                      </Grid>
                      {treesInfo?.length > 0 && (
                        <Table
                          title="Trees Info"
                          header={[
                            'id',
                            'Field Id',
                            'Position(row)',
                            'Position(column)',
                            'Tree Type',
                            'Price(Honey)',
                            'Air Supply',
                            'Reward CONE',
                          ]}
                          rows={treesInfo}
                          page={treesPage}
                          setPage={setTreesPage}
                          rowsPerPage={treesRowsPerPage}
                          setRowsPerPage={setTreesRowsPerPage}
                          totalCount={bearsInfo[0]?.trees?.length}
                          isActiveTable={isActiveTable === 'Trees Info'}
                          isSpinning={isTablesPaginationLoading}
                        />
                      )}
                      {decorationsInfo?.length > 0 && (
                        <Table
                          title="Decorations Info"
                          header={[
                            'Id',
                            'Field Id',
                            'Position(row)',
                            'Position(column)',
                            'Decoration Type',
                            'Price(Honey)',
                          ]}
                          rows={decorationsInfo}
                          page={decorationsPage}
                          setPage={setDecorationsPage}
                          rowsPerPage={decorationsRowsPerPage}
                          setRowsPerPage={setDecorationsRowsPerPage}
                          totalCount={bearsInfo[0]?.decorations?.length}
                          isActiveTable={isActiveTable === 'Decorations Info'}
                          isSpinning={isTablesPaginationLoading}
                        />
                      )}
                      {beesInfo?.length > 0 && (
                        <Table
                          title="Bees Info"
                          header={[
                            'Id',
                            'Name',
                            'Bee Type',
                            'Price(Honey)',
                            'Air Consume',
                            'Air Count Dependency',
                            'Body Size',
                            'Honey per hour',
                            'Honey Power Bee',
                          ]}
                          rows={beesInfo}
                          page={beesPage}
                          setPage={setBeesPage}
                          rowsPerPage={beesRowsPerPage}
                          setRowsPerPage={setBeesRowsPerPage}
                          totalCount={bearsInfo[0]?.bees?.length}
                          isActiveTable={isActiveTable === 'Bees Info'}
                          isSpinning={isTablesPaginationLoading}
                        />
                      )}
                      {apiariesInfo.length > 0 && (
                        <Table
                          title="Apiaries Info"
                          header={[
                            'Id',
                            'Position (row)',
                            'Position (column)',
                            'Space Occupied',
                            'Honey in Apiary',
                            'Honey Power',
                            'Apiary Type',
                            'Reward (delete)',
                            'Honey(max)',
                            'Price',
                            'Space Available',
                            'Bees history',
                          ]}
                          rows={apiariesInfo}
                          page={apiariesPage}
                          setPage={setApiariesPage}
                          rowsPerPage={apiariesRowsPerPage}
                          setRowsPerPage={setApiariesRowsPerPage}
                          totalCount={bearsInfo[0]?.apiaries?.length}
                          // customWidth="100%"
                          customCellWhiteSpace="pre"
                          isActiveTable={isActiveTable === 'Apiaries Info'}
                          isSpinning={isTablesPaginationLoading}
                        />
                      )}
                      {fieldsInfo?.length > 0 && (
                        <Table
                          title="Fields Info"
                          header={['Id', 'CountTiles', 'Field Type', 'PriceTile(Honey)']}
                          rows={fieldsInfo}
                          page={fieldsPage}
                          setPage={setFieldsPage}
                          rowsPerPage={fieldsRowsPerPage}
                          setRowsPerPage={setFieldsRowsPerPage}
                          totalCount={bearsInfo[0]?.fields?.length}
                          // customMt="0"
                          isActiveTable={isActiveTable === 'Fields Info'}
                          isSpinning={isTablesPaginationLoading}
                        />
                      )}
                      {fieldTilesInfo?.length > 0 && (
                        <Table
                          title="FieldTiles Info"
                          header={[]}
                          rows={fieldTilesInfo}
                          page={fieldTilesPage}
                          setPage={setFieldTilesPage}
                          rowsPerPage={fieldTilesRowsPerPage}
                          setRowsPerPage={setFieldTilesRowsPerPage}
                          totalCount={fieldTilesRowsTotalCount}
                          customCellWhiteSpace="pre"
                          customCellAlign="center"
                          isActiveTable={isActiveTable === 'Fields Info'}
                          isSpinning={isTablesPaginationLoading}
                        />
                      )}
                    </>
                  ) : (
                    <Grid container flexDirection="column" alignItems="flex-start">
                      <p className={classnames(styles.NotFoundText, styles.BearNotExistText)}>
                        The game has not started on this account yet
                      </p>
                    </Grid>
                  )}
                  {isTransactionsTablesLoading ? (
                    <BeeSpinnerLoading
                      classNames={{
                        Container: classnames(
                          styles.LoadingTablesContainer,
                          styles.LoadingTablesContainer__TransactionsTable,
                        ),
                        Spinner: styles.LoadingTablesSpinner,
                      }}
                    />
                  ) : latestTransactionsInfo?.length > 0 ? (
                    <Table
                      title="Latest Transactions"
                      header={['', 'Transaction', 'Block', 'Value', 'Action']}
                      rows={latestTransactionsInfo}
                      isActiveTable
                      customCellWhiteSpace="pre"
                      isSpinning={isTransactionsTablePaginationLoading}
                      onClickPrevButton={() => {
                        setLatestTransactionsPage(
                          latestTransactionsPage
                            ? latestTransactionsPage - 1
                            : latestTransactionsPage,
                        );
                      }}
                      onClickNextButton={() => {
                        setLatestTransactionsPage(latestTransactionsPage + 1);
                      }}
                      disabledPrevButton={
                        !latestTransactionsPage || isTransactionsTablePaginationLoading
                      }
                      page={latestTransactionsPage}
                      setPage={setLatestTransactionsPage}
                      rowsPerPage={transactionsRowsPerPage}
                      setRowsPerPage={setTransactionsRowsPerPage}
                      disabledNextButton={totalCount <= 10 || isTransactionsTablePaginationLoading}
                      isCollapsibleTable
                      isEnabledPaginationButtons
                      totalCount={totalCount}
                      classNames={{ TitleTable: styles.TitleTableLatestTransactions }}
                    />
                  ) : (
                    <p className={styles.EmptyTableTitle}>No transactions</p>
                  )}
                </Grid>
              </Grid>
            </>
          ) : (
            <Grid
              container
              flexDirection="column"
              alignItems={{ xs: 'center', sm: 'flex-start' }}
              mb={{ xs: '20px', sm: '40px', lg: '60px' }}
            >
              <div
                className={styles.NotFoundBackButton}
                onClick={() => {
                  navigate(-1);
                }}
                type="button"
                role="button"
                tabIndex="0"
                onKeyPress={() => {
                  navigate(-1);
                }}
              >
                &lt;<p className={styles.NotFoundBackLinkText}>Back</p>
              </div>
              <p className={styles.NotFoundText}>Oops! Nothing found</p>
              <div className={styles.DescriptionNotFoundText}>
                Oops! The search string you entered was:
                <p className={styles.DescriptionNotFoundText__address}>{location.state?.address}</p>
              </div>
              <p className={styles.DescriptionNotFoundText}>
                Sorry! This is an invalid search string.
              </p>
              <p className={styles.SearchTextFieldTextTitle}>You can search again</p>
              <TextField
                id="textField-header"
                variant="outlined"
                placeholder="Search Address / Nickname ..."
                onChange={onChangeAddress}
                onKeyPress={onKeyHandle}
                className={styles.TextField}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={SearchClick} size="large">
                      <SearchOutlined />
                    </IconButton>
                  ),
                }}
                sx={{
                  width: '100%',
                  maxWidth: { xs: '350px', sm: '400px', md: '500px' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                  '& .MuiButtonBase-root': {
                    color: '#F2A71E',
                  },
                }}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Layout>
  );
});

export default { AddressPageInfo, TitlePaper };
